import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LogIn, UserPlus } from 'lucide-react';

interface AccessGateProps {
  children: ReactNode;
}

export function AccessGate({ children }: AccessGateProps) {
  const { organization, isLoading: orgLoading, simulatedAuthState, isSuperAdmin } = useOrganization();
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Allow access to login page always
  if (location.pathname === '/login' || location.pathname === '/reset-password') {
    return <>{children}</>;
  }

  // Define public paths that don't need auth check before rendering
  const publicPaths = ['/', '/about', '/attendees', '/sponsors', '/nonprofits', 
    '/how-it-works', '/events', '/challenges', '/community', '/feature-requests'];
  
  const isPublicPath = publicPaths.includes(location.pathname) || 
    location.pathname.startsWith('/library');

  // For public paths with non-auth-required org, render immediately without waiting for auth
  if (isPublicPath && !orgLoading && !organization?.settings.require_auth) {
    return <>{children}</>;
  }

  // Show loading state only for protected routes that need auth check
  if (orgLoading || (authLoading && !isPublicPath)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Determine effective auth state:
  // - If super admin is simulating unauthenticated, treat as logged out
  // - Otherwise use actual auth state
  const isEffectivelyAuthenticated = simulatedAuthState === 'unauthenticated' 
    ? false 
    : !!user;

  // Check if entire site requires auth
  if (organization?.settings.require_auth && !isEffectivelyAuthenticated) {
    // If super admin is simulating logged out, show inline prompt instead of redirect
    if (isSuperAdmin && simulatedAuthState === 'unauthenticated') {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle>Login Required</CardTitle>
              <CardDescription>
                This site requires authentication to access.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                As a public visitor, you would see this login prompt.
              </p>
              <div className="flex gap-2 justify-center">
                <Button asChild variant="outline">
                  <Link to="/login">
                    <LogIn className="h-4 w-4 mr-2" />
                    Log In
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/login">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Sign Up
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if library specifically requires auth (granular gating)
  const isLibraryRoute = location.pathname === '/library' || location.pathname.startsWith('/library/');
  if (organization?.settings.library_requires_auth && isLibraryRoute && !isEffectivelyAuthenticated) {
    // If super admin is simulating logged out, show inline prompt
    if (isSuperAdmin && simulatedAuthState === 'unauthenticated') {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle>Library Access Restricted</CardTitle>
              <CardDescription>
                You need to be logged in to access the resource library.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                As a public visitor, you would see this login prompt when trying to access the library.
              </p>
              <div className="flex gap-2 justify-center">
                <Button asChild variant="outline">
                  <Link to="/login">
                    <LogIn className="h-4 w-4 mr-2" />
                    Log In
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/login">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Sign Up
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if planner specifically requires auth (granular gating)
  const isPlannerRoute = location.pathname === '/sandbox' || location.pathname.startsWith('/sandbox/');
  if (organization?.settings.planner_requires_auth && isPlannerRoute && !isEffectivelyAuthenticated) {
    // If super admin is simulating logged out, show inline prompt
    if (isSuperAdmin && simulatedAuthState === 'unauthenticated') {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle>Planner Access Restricted</CardTitle>
              <CardDescription>
                You need to be logged in to access the planner.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                As a public visitor, you would see this login prompt when trying to access the planner.
              </p>
              <div className="flex gap-2 justify-center">
                <Button asChild variant="outline">
                  <Link to="/login">
                    <LogIn className="h-4 w-4 mr-2" />
                    Log In
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/login">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Sign Up
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
